import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import api from "../../../api/axiosInstance";
import { Helmet } from "react-helmet";
import LogoWithHomeLink from "../../../components/LogoWithHomeLink";
import SitePageLeftHeading from "../../../components/SitePageLeftHeading";
import SiteRightContent from "../../../components/SiteRightContent";
import toast from "react-hot-toast";

export default function Initiate() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t("validation.email"))
      .max(100, t("validation.max", { max: 100 }))
      .required(t("validation.required")),
    mobile: Yup.string()
      .min(10, t("validation.mobile"))
      .max(10, t("validation.mobile"))
      .required(t("validation.required")),
  });

  const initialValues = {
    email: "",
    mobile: "",
  };

  const handleSubmit = async (
    values,
    { setErrors, setFieldError, setSubmitting }
  ) => {
    try {
      const response = await api.post("/api/v1/registration/create", values);

      navigate("/registration/verify-otp", { state: { data: response.data } });
    } catch (error) {
      const resp = error.response?.data;

      if (resp?.errors) {
        setErrors(resp.errors);
      }

      if (resp?.details) {
        Object.entries(resp.details).forEach(([field, messages]) => {
          setFieldError(field, messages[0]);
        });
      }

      if (resp?.message) {
        toast.error(resp.message);
      }
    }

    setSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>{t("pages.registration.initiate.title")}</title>
      </Helmet>
      <div className="flex flex-col flex-1 w-full lg:w-1/2">
        <LogoWithHomeLink />

        <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
          <SitePageLeftHeading
            title={t("pages.registration.initiate.page_title")}
            description={t("pages.registration.initiate.description")}
          />

          <div className="space-y-5">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {(props) => (
                <Form>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
                      {t("pages.registration.initiate.form.email")}
                    </label>
                    <Field
                      type="email"
                      name="email"
                      id="email"
                      maxLength={100}
                      placeholder="name@domain.com"
                      autoFocus
                      className={
                          (props.errors.email
                            ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                            : "border-gray-300 ") +
                          "dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-base text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                        }
                    />
                    <ErrorMessage
                      name="email"
                      component="p"
                      className="text-theme-xs text-error-500 mt-1.5"
                    />
                  </div>
                  <div className="mt-5">
                    <label htmlFor="mobile" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
                      {t("pages.registration.initiate.form.mobile")}
                    </label>
                    <Field
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      name="mobile"
                      id="mobile"
                      maxLength={10}
                      placeholder="0712345678"
                      className={
                          (props.errors.mobile
                            ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                            : "border-gray-300 ") +
                          "dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-base text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                        }
                    />
                    <ErrorMessage
                      name="mobile"
                      component="p"
                      className="text-theme-xs text-error-500 mt-1.5"
                    />
                  </div>

                  <div className="my-6">
                    <button
                      type="submit"
                      disabled={props.isSubmitting}
                      className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                    >
                      {props.isSubmitting
                        ? t("buttons.submitting")
                        : t("buttons.submit")}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      <SiteRightContent />
    </>
  );
}
